import api from '@/lib/api';
import type {
  SubscriptionPlan,
  Subscription,
  Payment,
  SubscriptionQueryDto,
  PaymentHistoryQueryDto,
  CreateSubscriptionDto,
  CreatePaymentIntentDto,
  CreateCheckoutSessionDto,
  SubscriptionPlanResponse,
  SubscriptionResponse,
  PaymentHistoryResponse,
  UserSubscriptionResponse,
  ProcessCheckoutSessionResponse,
} from '@/types/subscription';

class SubscriptionService {
  private readonly baseUrl = '/stripe';

  /**
   * Get all subscription plans from MongoDB with optional pagination and active filter
   */
  async getSubscriptionPlans(params?: {
    page?: number;
    limit?: number;
    isActive?: boolean;
  }): Promise<SubscriptionPlan[]> {
    try {
      const response = await api.get<SubscriptionPlanResponse>(
        `${this.baseUrl}/plans?isActive=true`,
        { params }
      );
      return response.data.data.plans;
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      throw new Error('Failed to fetch subscription plans');
    }
  }

  /**
   * Create a Stripe customer for the current user
   */
  async createCustomer(params: {
    email: string;
    metadata?: Record<string, string>;
  }): Promise<{ id: string; email?: string; name?: string }> {
    try {
      const response = await api.post<{
        success: boolean;
        data: { id: string; email?: string; name?: string };
      }>(`${this.baseUrl}/customers`, params);
      return response.data.data;
    } catch (error) {
      console.error('Error creating customer:', error);
      throw new Error('Failed to create customer');
    }
  }

  /**
   * Get subscription plans by organization (if supported)
   */
  async getSubscriptionPlansByOrganization(
    organizationId: string
  ): Promise<SubscriptionPlan[]> {
    try {
      const response = await api.get<SubscriptionPlanResponse>(
        `${this.baseUrl}/plans`,
        { params: { organizationId } }
      );
      return response.data.data.plans;
    } catch (error) {
      console.error('Error fetching organization subscription plans:', error);
      throw new Error('Failed to fetch organization subscription plans');
    }
  }

  /**
   * Get a single subscription plan by id
   */
  async getSubscriptionPlanById(id: string): Promise<SubscriptionPlan> {
    try {
      const response = await api.get<{
        success: boolean;
        data: SubscriptionPlan;
      }>(`${this.baseUrl}/plans/${id}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching subscription plan:', error);
      throw new Error('Failed to fetch subscription plan');
    }
  }

  /**
   * Get user subscriptions with pagination
   */
  async getUserSubscriptions(
    query: SubscriptionQueryDto
  ): Promise<UserSubscriptionResponse> {
    try {
      const response = await api.get<UserSubscriptionResponse>(
        `${this.baseUrl}/subscriptions`,
        { params: query }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching user subscriptions:', error);
      throw new Error('Failed to fetch user subscriptions');
    }
  }

  /**
   * Get organization subscriptions with pagination
   */
  async getOrganizationSubscriptions(
    query: SubscriptionQueryDto
  ): Promise<SubscriptionResponse> {
    try {
      const response = await api.get<SubscriptionResponse>(
        `${this.baseUrl}/subscriptions`,
        { params: query }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching organization subscriptions:', error);
      throw new Error('Failed to fetch organization subscriptions');
    }
  }

  /**
   * Get payment history with pagination
   */
  async getPaymentHistory(
    query: PaymentHistoryQueryDto
  ): Promise<PaymentHistoryResponse> {
    try {
      const response = await api.get<PaymentHistoryResponse>(
        `${this.baseUrl}/payments/history`,
        { params: query }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching payment history:', error);
      throw new Error('Failed to fetch payment history');
    }
  }

  /**
   * Create a new subscription
   */
  async createSubscription(data: CreateSubscriptionDto): Promise<Subscription> {
    try {
      const response = await api.post<{ success: boolean; data: Subscription }>(
        `${this.baseUrl}/subscriptions`,
        data
      );
      return response.data.data;
    } catch (error) {
      console.error('Error creating subscription:', error);
      throw new Error('Failed to create subscription');
    }
  }

  /**
   * Create a payment intent
   */
  async createPaymentIntent(
    data: CreatePaymentIntentDto
  ): Promise<{ clientSecret: string }> {
    try {
      const response = await api.post<{
        success: boolean;
        data: { clientSecret: string };
      }>(`${this.baseUrl}/payment-intents`, data);
      return response.data.data;
    } catch (error) {
      console.error('Error creating payment intent:', error);
      throw new Error('Failed to create payment intent');
    }
  }

  /**
   * Attach a payment method to a customer
   */
  async attachPaymentMethod(
    customerId: string,
    paymentMethodId: string
  ): Promise<{ id: string }> {
    try {
      const response = await api.post<{
        success: boolean;
        data: { id: string };
      }>(`${this.baseUrl}/customers/${customerId}/payment-methods`, {
        paymentMethodId,
      });
      return response.data.data;
    } catch (error) {
      console.error('Error attaching payment method:', error);
      throw new Error('Failed to attach payment method');
    }
  }

  /**
   * Set default payment method for a customer
   */
  async setDefaultPaymentMethod(
    customerId: string,
    paymentMethodId: string
  ): Promise<{ id: string }> {
    try {
      const response = await api.put<{
        success: boolean;
        data: { id: string };
      }>(`${this.baseUrl}/customers/${customerId}/default-payment-method`, {
        paymentMethodId,
      });
      return response.data.data;
    } catch (error) {
      console.error('Error setting default payment method:', error);
      throw new Error('Failed to set default payment method');
    }
  }

  /**
   * Create a checkout session
   */
  async createCheckoutSession(
    data: CreateCheckoutSessionDto
  ): Promise<{ url: string; id: string; status: string }> {
    try {
      const response = await api.post<{
        success: boolean;
        data: { url: string; id: string; status: string };
      }>(`${this.baseUrl}/checkout-sessions`, data);
      return response.data.data;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw new Error('Failed to create checkout session');
    }
  }

  /**
   * Cancel a subscription
   */
  async cancelSubscription(subscriptionId: string): Promise<void> {
    try {
      await api.delete(`${this.baseUrl}/subscriptions/${subscriptionId}`);
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw new Error('Failed to cancel subscription');
    }
  }

  /**
   * Update subscription quantity
   */
  async updateSubscriptionQuantity(
    subscriptionId: string,
    quantity: number
  ): Promise<Subscription> {
    try {
      const response = await api.put<{ success: boolean; data: Subscription }>(
        `${this.baseUrl}/subscriptions/${subscriptionId}/quantity`,
        { quantity }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error updating subscription quantity:', error);
      throw new Error('Failed to update subscription quantity');
    }
  }

  /**
   * Get subscription by ID
   */
  async getSubscriptionById(subscriptionId: string): Promise<Subscription> {
    try {
      const response = await api.get<{ success: boolean; data: Subscription }>(
        `${this.baseUrl}/subscriptions/${subscriptionId}`
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching subscription:', error);
      throw new Error('Failed to fetch subscription');
    }
  }

  /**
   * Get payment by ID
   */
  async getPaymentById(paymentId: string): Promise<Payment> {
    try {
      const response = await api.get<{ success: boolean; data: Payment }>(
        `${this.baseUrl}/payments/${paymentId}`
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching payment:', error);
      throw new Error('Failed to fetch payment');
    }
  }

  /**
   * Process checkout session and store subscription details
   * This is called immediately after successful checkout completion
   */
  async processCheckoutSession(
    sessionId: string
  ): Promise<ProcessCheckoutSessionResponse> {
    try {
      const response = await api.post(
        `${this.baseUrl}/process-checkout-session`,
        {
          sessionId,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error processing checkout session:', error);
      throw new Error('Failed to process checkout session');
    }
  }

  /**
   * Process webhook events
   */
  async processWebhook(
    event: Record<string, unknown>,
    signature: string
  ): Promise<void> {
    try {
      await api.post(`${this.baseUrl}/webhook`, event, {
        headers: {
          'stripe-signature': signature,
        },
      });
    } catch (error) {
      console.error('Error processing webhook:', error);
      throw new Error('Failed to process webhook');
    }
  }

  /**
   * Get pathways (for selection)
   */
  async getPathways(
    isActive: boolean = true
  ): Promise<
    Array<{ _id: string; name: string; key: string; isActive: boolean }>
  > {
    const params = new URLSearchParams();
    if (isActive !== undefined) params.append('isActive', isActive.toString());
    const response = await api.get(`/pathways?${params.toString()}`);
    return response.data;
  }
  async createPortalSession(returnUrl: string): Promise<{ url: string }> {
    try {
      const response = await api.post<{ success: boolean; data: { url: string } }>(
        `${this.baseUrl}/portal-session`,
        { returnUrl }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error creating portal session:', error);
      throw new Error('Failed to create portal session');
    }
  }

}


export default new SubscriptionService();
